import React, { useContext, useEffect, useMemo, useState } from "react";
import { connect } from "socket.io-client";
import { getToken } from "./auth-client";
import { useNullableCallToken } from "./callToken";

const API_HOST = import.meta.env.VITE_API_ORIGIN?.split("://").pop();

export type ServerToClientEvents = {
  updateConnectionTimeline: { id: string };
  updateMessageStatus: { id: string };
  approveConnection: { id: string };
  voiceCallDeclined: { callId: string; reason?: string };
  voiceCallEnded: { callId: string; userInitiated: boolean };
  newVoiceCall: { callId: string };
  videoCallInitialized: { callId: string };
  voiceCallInitializationFailed: { callId: string };
  inPersonVisitInitialized: { id: string };
  newInstantMessageReply: {
    callId: string;
    sender: string;
    content: string;
  };
};

// TODO: Socket<ServerToClientEvents, ClientToServerEvents> in v4
type TypedSocket = {
  /** register an event listener */
  on: <N extends keyof ServerToClientEvents>(
    event: N,
    listener: (data: ServerToClientEvents[N]) => void
  ) => void;
  /** remove an event listener */
  off: <N extends keyof ServerToClientEvents>(
    event: N,
    listener: (data: ServerToClientEvents[N]) => void
  ) => void;
};

const MockClient: TypedSocket = {
  on: () => null,
  off: () => null,
};

type ServerEventsContextType = {
  client: TypedSocket;
};

const ServerEventsContext = React.createContext<ServerEventsContextType>({
  client: MockClient,
});

function ServerEventsProvider({ children }: { children: React.ReactNode }) {
  // initialize a connected client
  const [client, setClient] = useState<TypedSocket>(MockClient);
  const token = getToken();
  const { callToken } = useNullableCallToken();
  useEffect(() => {
    if (!API_HOST) return undefined;
    if (!token && !callToken) return undefined;

    const auth = callToken
      ? { callToken }
      : { token: token.replace("Bearer ", "") };
    const socket = connect(API_HOST, {
      transports: ["websocket"],
      auth,
    });
    socket.on("connect", () => {
      // console.log({ connected: socket.connected });
    });
    // This handler can take an argument, containing the error,
    // if it wants to.
    socket.on("connect_error", () => {
      // console.error(e);
    });

    setClient(socket as TypedSocket);

    return () => {
      socket?.close();
      setClient(MockClient);
      return undefined;
    };
  }, [token, callToken]);

  // avoid context churn
  const value = useMemo(() => ({ client }), [client]);

  return (
    <ServerEventsContext.Provider value={value}>
      {children}
    </ServerEventsContext.Provider>
  );
}
export function useServerEvent<N extends keyof ServerToClientEvents>(
  name: N,
  fn: (data: ServerToClientEvents[N]) => void
) {
  const { client } = useContext(ServerEventsContext);
  useEffect(() => {
    client.on(name, fn);
    return () => {
      client.off(name, fn);
    };
  }, [client, fn, name]);
}

export default ServerEventsProvider;
